/**
 * Utilitaire pour générer le template Excel du brouillard de caisse (CECA).
 * Ce fichier est NOUVEAU et n'affecte pas les utilitaires existants.
 *
 * Format : DATE | REFERENCES | LIBELLES | ENTREES | SORTIES | SOLDES
 * La colonne SOLDES contient les formules de solde cumulé, comme dans
 * le modèle fourni par le client.
 */
import ExcelJS from "exceljs";

/**
 * Génère et télécharge le template Excel du brouillard de caisse
 */
export const generateBrouillardTemplate = async () => {
  try {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("ESPECE");

    // Colonnes au format CECA
    worksheet.columns = [
      { header: "DATE", key: "date", width: 14 },
      { header: "REFERENCES", key: "references", width: 18 },
      { header: "LIBELLES", key: "libelles", width: 35 },
      { header: "ENTREES", key: "entrees", width: 15 },
      { header: "SORTIES", key: "sorties", width: 15 },
      { header: "SOLDES", key: "soldes", width: 15 },
    ];

    // Styliser l'en-tête
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF009688" },
    };
    headerRow.alignment = { vertical: "middle", horizontal: "center" };
    headerRow.height = 25;

    // Lignes d'exemple
    const exampleRows = [
      {
        date: "02/01/2026",
        references: "CPT-001",
        libelles: "Encaissement comptoir",
        entrees: 150000,
        sorties: "",
      },
      {
        date: "03/01/2026",
        references: "FACT-12",
        libelles: "Achat fournitures bureau",
        entrees: "",
        sorties: 25000,
      },
      {
        date: "05/01/2026",
        references: "DIV-03",
        libelles: "Transport marchandise",
        entrees: "",
        sorties: 10000,
      },
    ];

    exampleRows.forEach((row) => worksheet.addRow(row));

    // Formules SOLDES : première ligne = ENTREES - SORTIES,
    // suivantes = solde precedent + ENTREES - SORTIES (comme le modèle CECA)
    const dataCount = exampleRows.length;
    for (let i = 2; i <= dataCount + 1; i++) {
      const cell = worksheet.getCell(`F${i}`);
      cell.value = {
        formula: i === 2 ? `D${i}-E${i}` : `F${i - 1}+D${i}-E${i}`,
      };
      cell.numFmt = "#,##0";
    }
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > 1) {
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin", color: { argb: "FFD3D3D3" } },
            left: { style: "thin", color: { argb: "FFD3D3D3" } },
            bottom: { style: "thin", color: { argb: "FFD3D3D3" } },
            right: { style: "thin", color: { argb: "FFD3D3D3" } },
          };
          cell.alignment = { vertical: "middle" };
        });
      }
    });
    ["D", "E"].forEach((col) => {
      for (let i = 2; i <= dataCount + 1; i++) {
        worksheet.getCell(`${col}${i}`).numFmt = "#,##0";
      }
    });

    // Feuille d'instructions
    const instructionsSheet = workbook.addWorksheet("Instructions");
    instructionsSheet.columns = [{ width: 80 }];

    const instructions = [
      {
        text: "INSTRUCTIONS POUR L'IMPORT DU BROUILLARD DE CAISSE",
        bold: true,
        size: 16,
        color: "009688",
      },
      { text: "" },
      { text: "FORMAT DU FICHIER :", bold: true, size: 12 },
      { text: "• Une seule feuille, en-tête : DATE | REFERENCES | LIBELLES | ENTREES | SORTIES | SOLDES" },
      { text: "• Chaque ligne = une opération de caisse (encaissement ou décaissement)" },
      { text: "" },
      { text: "COLONNES :", bold: true, size: 12 },
      { text: "1. DATE - OBLIGATOIRE (JJ/MM/AAAA ou date Excel)" },
      { text: "2. REFERENCES - Optionnel (numéro de pièce, référence interne)" },
      { text: "3. LIBELLES - OBLIGATOIRE (description de l'opération)" },
      { text: "4. ENTREES - montant encaissé (> 0)" },
      { text: "5. SORTIES - montant décaissé (> 0)" },
      { text: "6. SOLDES - ignoré à l'import (formule de solde cumulé)" },
      { text: "" },
      { text: "RÈGLES :", bold: true, size: 12 },
      { text: "• Une ligne ne peut pas avoir à la fois une entrée et une sortie" },
      { text: "• Les lignes vides sont ignorées" },
      { text: "• Import tout ou rien : une ligne invalide annule tout l'import" },
      { text: "• Chaque ligne créée est réglée comptant en caisse (compte 571)" },
      { text: "" },
      { text: "FORMATS ACCEPTÉS :", bold: true, size: 12 },
      { text: ".xlsx, .xls (10 MB max)" },
    ];

    instructions.forEach((instruction, index) => {
      const row = instructionsSheet.getRow(index + 1);
      row.getCell(1).value = instruction.text;
      if (instruction.bold) {
        row.getCell(1).font = {
          bold: true,
          size: instruction.size || 11,
          color: instruction.color
            ? { argb: `FF${instruction.color}` }
            : undefined,
        };
      }
      row.getCell(1).alignment = { vertical: "top", wrapText: true };
      row.height = 20;
    });

    // Télécharger
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "modele_brouillard_de_caisse.xlsx");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    console.error("Erreur lors de la génération du template brouillard:", error);
    throw error;
  }
};

export default {
  generateBrouillardTemplate,
};
